import type { Cart, CartItem } from "../interfaces/cart";
import currency from "currency.js";
import { getCookie, deleteCookie, setCookie } from "./cookies";
import { toInteger } from "./number";

const defaultCartName: string = process.env.CART_NAME ?? "shopping-cart";

/**
 * Save Cart (actually the cart ID) on client (useful for non-logged-in users).
 * This helps us track the cart until the user logs in or checks out.
 */
export function saveCartIdOnClient(cartName: string, cartId: string) {
  setCookie(cartName, cartId);
}

export function getLocalCartId(cartName: string): string {
  return getCookie(cartName) as unknown as string;
}

export function deleteLocalCartId(cartName: string) {
  deleteCookie(cartName);
}

export function setDefaultCartId(cartId: string): void {
  saveCartIdOnClient(defaultCartName, cartId);
}

export function deleteDefaultCartId(): void {
  deleteLocalCartId(defaultCartName);
}

export function getDefaultCartId(): string {
  return getLocalCartId(defaultCartName);
}

export function itemExistsInCart(cart: Cart, itemId: string): boolean {
  return Boolean(cart.items.find(item => item.id === itemId));
}

export function calculateCost(cartItems: CartItem[]) { 
  const items = cartItems;
  const totalPrice = calculateTotalCost(items, "price");
  const totalSellingPrice = calculateTotalCost(items, "discountedPrice");

  return { totalPrice, totalSellingPrice };
}

export function countTotalItemsInCart(cart: Cart) {
  const items = cart.items;
  const count = items.reduce((acc, item) => {
    acc += item.quantity;
    return acc;
  }, 0);

  return count;
}

export function countUniqueItemsInCart(cart: Cart) {
  const items = cart.items;
  const count = items.length;

  return count;
}


// Helpers
function calculateTotalCost(items, priceField: string) {
  if(!items.every(item => Number(item[priceField]))) {
    return 0;
  }

  /* This might work in some situations. But we may run into
     JavaScript number issues when dealing with some floating point arithmentc.
  return items.reduce((total, item) => {
    return total + parseFloat(item[priceField]) * toInteger(item.quantity);
  }, 0).toPrecision(4);
  */

  // I'm returning the .value at the end so we have the raw number
  // not the object currency object itself even though that works
  // with Intl.NumberFormat, we may need the raw number in other situations
  // where we are not using Intl.NumberFormat.
  // Plus, after performing this calculation, the caller expects a number
  // not an object.
  return items.reduce((total, item) => {
    return (
      total.add(
        currency(item[priceField]).multiply(toInteger(item.quantity))
      )
    );
  }, currency(0)).value;
}
